import { ChangeDetectorRef, Component, DestroyRef, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { HttpErrorResponse } from '@angular/common/http';
import { ActivatedRoute, Router } from '@angular/router';
import { of, Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, switchMap } from 'rxjs/operators';
import { CurrentUserService } from 'src/app/services/current-user.service';
import { ToastService } from 'src/app/services/toast.service';
import { SocialFeedService } from '../social-feed/services/social-feed.service';
import {
  CommunityBan,
  CommunityInvite,
  CommunityInviteLink,
  CommunityJoinRequest,
  CommunityLogEntry,
  CommunityMember,
  SocialAuthor,
  SocialCommunity,
  SocialPost,
} from '../social-feed/models/social-feed.model';
import { SocialHubService } from '../social-feed/services/social-hub.service';
import { communityError, LOG_LABELS } from './community-utils';

type CommunityTab = 'posts' | 'about' | 'members' | 'requests' | 'invite' | 'moderation';
const MANAGER_TABS: CommunityTab[] = ['requests', 'invite', 'moderation'];

/**
 * One community: its posts, about, members, and (for owners and moderators)
 * join requests, invites and links, bans and the log. Who may do what is
 * decided by the server; the page only hides buttons that would be refused.
 * The open tab is kept in the address (?tab=) so notifications can link to it.
 */
@Component({
  selector: 'app-community',
  templateUrl: './community.component.html',
  styleUrls: ['./community.component.scss'],
  standalone: false,
})
export class CommunityComponent implements OnInit {
  slug = '';
  community: SocialCommunity | null = null;
  loading = true;
  notFound = false;
  /** Loading failed for another reason (offline, server error): offer a retry. */
  loadFailed = false;
  activeTab: CommunityTab = 'posts';
  currentUserId: number | null = null;
  userName = '';

  /** Join / leave / request / invite answer in flight. */
  busy = false;
  joinMessage = '';
  showRequestForm = false;

  // ---- posts ----
  posts: SocialPost[] = [];
  postsLoading = false;
  postsError = false;
  postsPage = 1;
  hasMorePosts = false;
  loadingMore = false;

  // ---- members ----
  members: CommunityMember[] = [];
  membersLoading = false;
  membersPage = 1;
  hasMoreMembers = false;
  memberQuery = '';
  private memberSearch$ = new Subject<string>();
  /** The member whose action menu is open (user id). */
  memberMenuFor: number | null = null;
  pendingMemberAction: { member: CommunityMember; kind: 'remove' | 'ban' | 'transfer' } | null = null;
  memberActionBusy = false;

  // ---- requests ----
  requests: CommunityJoinRequest[] = [];
  requestsLoading = false;
  hasMoreRequests = false;
  requestsPage = 1;
  deciding = new Set<number>();

  // ---- invite ----
  inviteQuery = '';
  inviteResults: SocialAuthor[] = [];
  inviteSearching = false;
  private inviteSearch$ = new Subject<string>();
  invitedIds = new Set<number>();
  invites: CommunityInvite[] = [];
  invitesLoading = false;
  links: CommunityInviteLink[] = [];
  linksLoading = false;
  linkDays: number | null = 7;
  linkMaxUses: number | null = null;
  creatingLink = false;
  copiedLinkId: number | null = null;

  // ---- moderation ----
  bans: CommunityBan[] = [];
  bansLoading = false;
  log: CommunityLogEntry[] = [];
  logLoading = false;
  hasMoreLog = false;
  logPage = 1;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private social: SocialFeedService,
    private currentUserService: CurrentUserService,
    private toaster: ToastService,
    private hub: SocialHubService,
    private cdr: ChangeDetectorRef,
    private destroyRef: DestroyRef
  ) {}

  ngOnInit(): void {
    this.currentUserService.currentUser.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((user) => {
      this.currentUserId = user?.id ?? null;
      this.userName = user?.display_name || user?.name || '';
    });

    this.memberSearch$
      .pipe(debounceTime(300), distinctUntilChanged(), takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.loadMembers());

    this.inviteSearch$
      .pipe(
        debounceTime(300),
        distinctUntilChanged(),
        switchMap((q) => {
          this.inviteSearching = q.length >= 2;
          this.cdr.detectChanges();
          return q.length >= 2 ? this.social.search(q) : of({ people: [] as SocialAuthor[] });
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe({
        next: (res: any) => {
          this.inviteResults = (res?.people || []).filter((p: SocialAuthor) => p.id !== this.currentUserId);
          this.inviteSearching = false;
          this.cdr.detectChanges();
        },
        error: () => {
          this.inviteSearching = false;
          this.cdr.detectChanges();
        },
      });

    this.route.paramMap.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((params) => {
      this.slug = params.get('slug') || '';
      this.reset();
      this.loadCommunity(true);
    });
  }

  private reset(): void {
    this.community = null;
    this.posts = [];
    this.members = [];
    this.requests = [];
    this.invites = [];
    this.links = [];
    this.bans = [];
    this.log = [];
    this.notFound = false;
    this.loadFailed = false;
  }

  // ---------------------------------------------------------------------------
  // Loading
  // ---------------------------------------------------------------------------

  loadCommunity(first = false): void {
    if (first) this.loading = true;
    this.social.getCommunity(this.slug).subscribe({
      next: (community) => {
        this.community = community;
        this.loading = false;
        if (first) {
          const wanted = this.route.snapshot.queryParamMap.get('tab') as CommunityTab | null;
          this.selectTab(wanted && this.tabAllowed(wanted) ? wanted : 'posts', false);
        } else if (!this.tabAllowed(this.activeTab)) {
          this.selectTab('about', false);
        }
        this.cdr.detectChanges();
      },
      error: (err: HttpErrorResponse) => {
        this.loading = false;
        if (!this.community) {
          this.notFound = err?.status === 404;
          this.loadFailed = err?.status !== 404;
        }
        this.cdr.detectChanges();
      },
    });
  }

  retryLoad(): void {
    this.loadFailed = false;
    this.loadCommunity(true);
  }

  tabAllowed(tab: CommunityTab): boolean {
    const c = this.community;
    if (!c) return false;
    if (tab === 'posts' || tab === 'members') return c.can_see_posts;
    if (tab === 'invite') return c.can_invite;
    if (MANAGER_TABS.includes(tab)) return c.can_manage;
    return true;
  }

  selectTab(tab: CommunityTab, updateUrl = true): void {
    if (!this.tabAllowed(tab)) {
      tab = 'about';
    }
    this.activeTab = tab;
    if (updateUrl) {
      this.router.navigate([], { queryParams: { tab: tab === 'posts' ? null : tab }, replaceUrl: true });
    }
    if (tab === 'posts' && !this.posts.length) this.loadPosts();
    if (tab === 'members' && !this.members.length) this.loadMembers();
    if (tab === 'requests') this.loadRequests();
    if (tab === 'invite') {
      if (this.community?.can_manage) {
        this.loadInvites();
        this.loadLinks();
      }
    }
    if (tab === 'moderation') {
      this.loadBans();
      this.loadLog();
    }
  }

  private fail(err: HttpErrorResponse, fallback: string): void {
    this.toaster.showNote(communityError(err, fallback), 'danger');
  }

  // ---------------------------------------------------------------------------
  // Joining
  // ---------------------------------------------------------------------------

  private act(call: ReturnType<SocialFeedService['getCommunity']>, done?: string): void {
    if (this.busy) return;
    this.busy = true;
    call.subscribe({
      next: (community) => {
        const wasMember = !!this.community?.my_role;
        this.community = community;
        this.busy = false;
        this.showRequestForm = false;
        this.joinMessage = '';
        if (done) this.toaster.showSuccess(done);
        this.hub.refreshCommunities();
        // Joining opens the posts; leaving a private one hides them.
        if (!wasMember && community.my_role) {
          this.posts = [];
          this.selectTab('posts');
        } else if (wasMember && !community.my_role) {
          this.posts = [];
          this.members = [];
          this.selectTab(community.can_see_posts ? 'posts' : 'about');
        }
        this.cdr.detectChanges();
      },
      error: (err) => {
        this.busy = false;
        this.fail(err, 'Something went wrong. Please try again.');
        this.loadCommunity();
        this.cdr.detectChanges();
      },
    });
  }

  join(): void {
    this.act(this.social.joinCommunity(this.slug), `Welcome to ${this.community?.name}!`);
  }

  sendRequest(): void {
    this.act(this.social.requestToJoin(this.slug, this.joinMessage.trim()), 'Request sent. A moderator will review it.');
  }

  cancelRequest(): void {
    this.act(this.social.cancelJoinRequest(this.slug));
  }

  acceptInvite(): void {
    const id = this.community?.my_invite_id;
    if (id) this.act(this.social.answerCommunityInvite(id, true), `Welcome to ${this.community?.name}!`);
  }

  declineInvite(): void {
    const id = this.community?.my_invite_id;
    if (id) this.act(this.social.answerCommunityInvite(id, false));
  }

  pendingLeave = false;

  confirmLeave(): void {
    this.pendingLeave = false;
    this.act(this.social.leaveCommunity(this.slug), `You left ${this.community?.name}.`);
  }

  // ---------------------------------------------------------------------------
  // Posts
  // ---------------------------------------------------------------------------

  loadPosts(): void {
    if (!this.community) return;
    this.postsLoading = true;
    this.postsError = false;
    this.social.getFeed({ community: this.community.id }).subscribe({
      next: (page) => {
        this.posts = page.results;
        this.postsPage = 1;
        this.hasMorePosts = !!page.next;
        this.postsLoading = false;
        this.cdr.detectChanges();
      },
      error: () => {
        this.posts = [];
        this.postsLoading = false;
        this.postsError = true;
        this.cdr.detectChanges();
      },
    });
  }

  loadMorePosts(): void {
    if (this.loadingMore || !this.hasMorePosts || !this.community) return;
    this.loadingMore = true;
    const next = this.postsPage + 1;
    this.social.getFeed({ community: this.community.id, page: next }).subscribe({
      next: (page) => {
        const have = new Set(this.posts.map((p) => p.id));
        this.posts = [...this.posts, ...page.results.filter((p) => !have.has(p.id))];
        this.postsPage = next;
        this.hasMorePosts = !!page.next;
        this.loadingMore = false;
        this.cdr.detectChanges();
      },
      error: () => {
        this.loadingMore = false;
        this.cdr.detectChanges();
      },
    });
  }

  onRemoved(id: number): void {
    this.posts = this.posts.filter((p) => p.id !== id);
  }

  openProfile(userId: number): void {
    this.router.navigate(['/social-profile', userId]);
  }

  // ---------------------------------------------------------------------------
  // Members
  // ---------------------------------------------------------------------------

  onMemberQuery(value: string): void {
    this.memberSearch$.next(value.trim());
  }

  loadMembers(more = false): void {
    this.membersLoading = !more;
    const page = more ? this.membersPage + 1 : 1;
    this.social.getCommunityMembers(this.slug, { q: this.memberQuery.trim() || undefined, page }).subscribe({
      next: (res) => {
        this.members = more ? [...this.members, ...res.results] : res.results;
        this.membersPage = page;
        this.hasMoreMembers = !!res.next;
        this.membersLoading = false;
        this.cdr.detectChanges();
      },
      error: () => {
        this.membersLoading = false;
        this.cdr.detectChanges();
      },
    });
  }

  /** Owners act on anyone but themselves; moderators only on members. */
  canActOn(member: CommunityMember): boolean {
    const mine = this.community?.my_role;
    if (member.user.id === this.currentUserId || member.role === 'owner') return false;
    return mine === 'owner' || (mine === 'moderator' && member.role === 'member');
  }

  toggleMemberMenu(member: CommunityMember): void {
    this.memberMenuFor = this.memberMenuFor === member.user.id ? null : member.user.id;
  }

  setRole(member: CommunityMember, role: 'moderator' | 'member'): void {
    this.memberMenuFor = null;
    this.social.setCommunityRole(this.slug, member.user.id, role).subscribe({
      next: () => {
        member.role = role;
        this.toaster.showSuccess(
          role === 'moderator' ? `${member.user.name} is now a moderator.` : `${member.user.name} is no longer a moderator.`
        );
        this.cdr.detectChanges();
      },
      error: (err) => this.fail(err, "Couldn't change their role."),
    });
  }

  askMemberAction(member: CommunityMember, kind: 'remove' | 'ban' | 'transfer'): void {
    this.memberMenuFor = null;
    this.pendingMemberAction = { member, kind };
  }

  get memberActionTitle(): string {
    const a = this.pendingMemberAction;
    if (!a) return '';
    const name = a.member.user.name;
    return a.kind === 'transfer' ? `Make ${name} the owner?` : a.kind === 'ban' ? `Ban ${name}?` : `Remove ${name}?`;
  }

  get memberActionMessage(): string {
    const a = this.pendingMemberAction;
    if (!a) return '';
    if (a.kind === 'transfer') {
      return "They'll control the community, including deleting it. You'll stay on as a moderator.";
    }
    if (a.kind === 'ban') {
      return "They'll be removed and can't join again, ask to join, or use an invite link until they're unbanned.";
    }
    return 'They can join again later (or ask to, if the community is private).';
  }

  confirmMemberAction(): void {
    const a = this.pendingMemberAction;
    if (!a || this.memberActionBusy) return;
    this.memberActionBusy = true;
    const done = () => {
      this.memberActionBusy = false;
      this.pendingMemberAction = null;
      this.cdr.detectChanges();
    };
    if (a.kind === 'transfer') {
      this.social.transferCommunity(this.slug, a.member.user.id).subscribe({
        next: (community) => {
          this.community = community;
          this.toaster.showSuccess(`${a.member.user.name} now owns ${community.name}.`);
          this.loadMembers();
          done();
        },
        error: (err) => {
          this.fail(err, "Couldn't hand the community over.");
          done();
        },
      });
      return;
    }
    this.social.removeCommunityMember(this.slug, a.member.user.id, a.kind === 'ban').subscribe({
      next: () => {
        this.members = this.members.filter((m) => m.user.id !== a.member.user.id);
        if (this.community) this.community.member_count = Math.max(0, this.community.member_count - 1);
        this.toaster.showSuccess(a.kind === 'ban' ? `${a.member.user.name} was banned.` : `${a.member.user.name} was removed.`);
        done();
      },
      error: (err) => {
        this.fail(err, "Couldn't remove them.");
        done();
      },
    });
  }

  // ---------------------------------------------------------------------------
  // Requests
  // ---------------------------------------------------------------------------

  loadRequests(more = false): void {
    this.requestsLoading = !more;
    const page = more ? this.requestsPage + 1 : 1;
    this.social.getJoinRequests(this.slug, page).subscribe({
      next: (res) => {
        this.requests = more ? [...this.requests, ...res.results] : res.results;
        this.requestsPage = page;
        this.hasMoreRequests = !!res.next;
        this.requestsLoading = false;
        if (this.community) this.community.pending_requests = res.count;
        this.cdr.detectChanges();
      },
      error: () => {
        this.requestsLoading = false;
        this.cdr.detectChanges();
      },
    });
  }

  decide(req: CommunityJoinRequest, approve: boolean): void {
    if (this.deciding.has(req.id)) return;
    this.deciding.add(req.id);
    this.social.decideJoinRequest(this.slug, req.id, approve).subscribe({
      next: () => {
        this.deciding.delete(req.id);
        this.requests = this.requests.filter((r) => r.id !== req.id);
        if (this.community) {
          this.community.pending_requests = Math.max(0, (this.community.pending_requests || 1) - 1);
          if (approve) this.community.member_count += 1;
        }
        if (approve) this.members = [];
        this.cdr.detectChanges();
      },
      error: (err) => {
        this.deciding.delete(req.id);
        this.fail(err, "Couldn't answer that request.");
        // Someone else may have answered it already: show the real list.
        this.loadRequests();
      },
    });
  }

  // ---------------------------------------------------------------------------
  // Invites and links
  // ---------------------------------------------------------------------------

  onInviteQuery(value: string): void {
    this.inviteSearch$.next(value.trim());
  }

  sendInvite(person: SocialAuthor): void {
    if (this.invitedIds.has(person.id)) return;
    this.invitedIds.add(person.id);
    this.social.inviteToCommunity(this.slug, person.id).subscribe({
      next: () => {
        this.toaster.showSuccess(`Invite sent to ${person.name}.`);
        if (this.community?.can_manage) this.loadInvites();
        this.cdr.detectChanges();
      },
      error: (err) => {
        this.invitedIds.delete(person.id);
        this.fail(err, "Couldn't send the invite.");
        this.cdr.detectChanges();
      },
    });
  }

  loadInvites(): void {
    this.invitesLoading = true;
    this.social.getCommunityInvites(this.slug).subscribe({
      next: (res) => {
        this.invites = res.results;
        this.invitesLoading = false;
        this.cdr.detectChanges();
      },
      error: () => {
        this.invitesLoading = false;
        this.cdr.detectChanges();
      },
    });
  }

  revokeInvite(inv: CommunityInvite): void {
    this.social.revokeCommunityInvite(this.slug, inv.id).subscribe({
      next: () => {
        this.invites = this.invites.filter((i) => i.id !== inv.id);
        this.invitedIds.delete(inv.invitee.id);
        this.cdr.detectChanges();
      },
      error: (err) => this.fail(err, "Couldn't cancel the invite."),
    });
  }

  loadLinks(): void {
    this.linksLoading = true;
    this.social.getCommunityLinks(this.slug).subscribe({
      next: (res) => {
        this.links = res.results;
        this.linksLoading = false;
        this.cdr.detectChanges();
      },
      error: () => {
        this.linksLoading = false;
        this.cdr.detectChanges();
      },
    });
  }

  createLink(): void {
    if (this.creatingLink) return;
    this.creatingLink = true;
    this.social.createCommunityLink(this.slug, this.linkDays, this.linkMaxUses).subscribe({
      next: (link) => {
        this.links = [link, ...this.links];
        this.creatingLink = false;
        this.copyLink(link);
        this.cdr.detectChanges();
      },
      error: (err) => {
        this.creatingLink = false;
        this.fail(err, "Couldn't make the link.");
        this.cdr.detectChanges();
      },
    });
  }

  linkUrl(link: CommunityInviteLink): string {
    return `${window.location.origin}${this.router.serializeUrl(this.router.createUrlTree(['/communities/join', link.token]))}`;
  }

  copyLink(link: CommunityInviteLink): void {
    const url = this.linkUrl(link);
    const done = () => {
      this.copiedLinkId = link.id;
      this.toaster.showSuccess('Link copied.');
      this.cdr.detectChanges();
      setTimeout(() => {
        if (this.copiedLinkId === link.id) this.copiedLinkId = null;
        this.cdr.detectChanges();
      }, 2500);
    };
    if (navigator.clipboard?.writeText) {
      navigator.clipboard.writeText(url).then(done, () => window.prompt('Copy this link:', url));
    } else {
      window.prompt('Copy this link:', url);
    }
  }

  revokeLink(link: CommunityInviteLink): void {
    this.social.revokeCommunityLink(this.slug, link.id).subscribe({
      next: () => {
        link.usable = false;
        this.cdr.detectChanges();
      },
      error: (err) => this.fail(err, "Couldn't turn the link off."),
    });
  }

  // ---------------------------------------------------------------------------
  // Moderation
  // ---------------------------------------------------------------------------

  loadBans(): void {
    this.bansLoading = true;
    this.social.getCommunityBans(this.slug).subscribe({
      next: (res) => {
        this.bans = res.results;
        this.bansLoading = false;
        this.cdr.detectChanges();
      },
      error: () => {
        this.bansLoading = false;
        this.cdr.detectChanges();
      },
    });
  }

  unban(ban: CommunityBan): void {
    this.social.unbanFromCommunity(this.slug, ban.user.id).subscribe({
      next: () => {
        this.bans = this.bans.filter((b) => b.user.id !== ban.user.id);
        this.toaster.showSuccess(`${ban.user.name} was unbanned.`);
        this.loadLog();
        this.cdr.detectChanges();
      },
      error: (err) => this.fail(err, "Couldn't unban them."),
    });
  }

  loadLog(more = false): void {
    this.logLoading = !more;
    const page = more ? this.logPage + 1 : 1;
    this.social.getCommunityLog(this.slug, page).subscribe({
      next: (res) => {
        this.log = more ? [...this.log, ...res.results] : res.results;
        this.logPage = page;
        this.hasMoreLog = !!res.next;
        this.logLoading = false;
        this.cdr.detectChanges();
      },
      error: () => {
        this.logLoading = false;
        this.cdr.detectChanges();
      },
    });
  }

  logText(entry: CommunityLogEntry): string {
    const actor = entry.actor?.name || 'Someone';
    const target = entry.target_user?.name || '';
    const label = LOG_LABELS[entry.action] || entry.action.replace(/_/g, ' ');
    return `${actor} ${label}${target ? ' ' + target : ''}${entry.details && entry.action !== 'updated' ? ` — ${entry.details}` : ''}`;
  }

  // ---------------------------------------------------------------------------
  // Helpers
  // ---------------------------------------------------------------------------

  initials(name: string): string {
    return (name || '')
      .split(/\s+/)
      .map((w) => w[0])
      .filter(Boolean)
      .slice(0, 2)
      .join('')
      .toUpperCase();
  }

  imageUrl(path: string | null | undefined): string {
    return this.social.resolveImageUrl(path);
  }

  timeAgo(isoDate: string): string {
    const minutes = Math.floor((Date.now() - new Date(isoDate).getTime()) / 60000);
    if (minutes < 1) return 'now';
    if (minutes < 60) return `${minutes}m`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours}h`;
    return `${Math.floor(hours / 24)}d`;
  }

  roleLabel(role: string): string {
    return role === 'owner' ? 'Owner' : role === 'moderator' ? 'Moderator' : 'Member';
  }
}
